import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { bootstrapGet } from '../utils/bootstrapApi';
import { useAuth } from './AuthContext';

const AccessManifestContext = createContext(null);

const EMPTY_MANIFEST = {
  platformDeveloper: false,
  allowedRoutes: [],
  capabilities: [],
  bundles: ['common'],
  crossAreaExceptions: [],
  accessProfile: null,
  defaultLanding: null,
  featureFlags: {
    enableCapabilityRouting: false,
    enableAreaBundles: false,
    enableProgressiveDataLoading: false,
    enableCrossAreaExceptions: false,
  },
  // Tender access is per-user *per space* (a live TenderAccessGrant), so it
  // cannot be a route capability. Default closed: no signal, no workspace.
  tender: { hasAccess: false, visibleCount: 0, openCount: 0 },
};

export const AccessManifestProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [manifest, setManifest] = useState(EMPTY_MANIFEST);
  const [loaded, setLoaded] = useState(false);

  const loadManifest = useCallback(async () => {
    // Manifest is JWT-authenticated (`get_current_user` resolves the user by `sub`).
    // Do not wait for `/users/me` numeric `id` (R23 placeholder) or the app stays on "Loading access…" forever.
    if (!token) {
      setManifest(EMPTY_MANIFEST);
      setLoaded(true);
      return;
    }
    setLoaded(false);
    try {
      const response = await bootstrapGet('/access/manifest');
      setManifest({ ...EMPTY_MANIFEST, ...(response?.data || {}) });
    } catch (error) {
      console.error('Error loading access manifest:', error);
      setManifest(EMPTY_MANIFEST);
    } finally {
      setLoaded(true);
    }
  }, [token]);

  useEffect(() => {
    loadManifest();
  }, [loadManifest]);

  const hasCapability = useCallback(
    (capability) => {
      if (!capability) return true;
      if (capability === 'platform.developer' || capability.startsWith('platform.')) {
        return (
          manifest.platformDeveloper === true
          || manifest.capabilities.includes('platform.developer')
        );
      }
      // Align with MenuAccessContext: superadmin bypasses tenant capability checks.
      if (user?.role === 'superadmin') return true;
      return manifest.capabilities.includes(capability);
    },
    [manifest.capabilities, manifest.platformDeveloper, user?.role]
  );

  const hasBundle = useCallback(
    (bundle) => {
      if (!bundle) return true;
      return manifest.bundles.includes(bundle);
    },
    [manifest.bundles]
  );

  /**
   * Whether to offer the tender workspace, and how much is in it.
   *
   * Deliberately not routed through `hasCapability`: the superadmin exception
   * (they hold no grant by design but must be able to break-glass) is decided
   * once on the server, in tender_access.nav_signal. Repeating it here would
   * put the same rule in two places that could drift apart.
   */
  const tenderAccess = useMemo(
    () => manifest.tender || EMPTY_MANIFEST.tender,
    [manifest.tender]
  );

  const value = useMemo(
    () => ({
      manifest,
      loaded,
      hasCapability,
      hasBundle,
      tenderAccess,
      refreshManifest: loadManifest,
    }),
    [manifest, loaded, hasCapability, hasBundle, tenderAccess, loadManifest]
  );

  return (
    <AccessManifestContext.Provider value={value}>
      {children}
    </AccessManifestContext.Provider>
  );
};

export const useAccessManifest = () => {
  const context = useContext(AccessManifestContext);
  if (!context) {
    throw new Error('useAccessManifest must be used within an AccessManifestProvider');
  }
  return context;
};

export default AccessManifestContext;
